import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { LuChevronRight } from 'react-icons/lu';
import { BsThreeDotsVertical } from 'react-icons/bs';
import classNames from 'classnames';
import { APP_BG } from '@/constant/theme/colors.constant';
import AppButton from './AppButton';
import { tid } from '@/utils/testId';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface RowAction {
  /** Display label */
  label: string;
  /** Leading icon (16×16 recommended) */
  icon: React.ReactNode;
  /** When true: item is dimmed and non-clickable */
  disabled?: boolean;
  /** Tooltip / native title shown on hover */
  tooltip?: string;
  /** Danger styling — red icon + label */
  danger?: boolean;
  /** Insert a divider + optional section title before this item */
  divider?: boolean;
  /** Section label shown above the divider group */
  sectionLabel?: string;
  onClick?: () => void;
}

export interface RowActionsMenuProps {
  actions: RowAction[];
  /** Optional title shown in the menu header */
  menuTitle?: string;
  /** Menu open direction */
  placement?: 'left' | 'right';
  /** Unique id used for test selectors */
  testId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────
export const RowActionsMenu = ({
  actions,
  placement = 'left',
  testId,
  menuTitle,
}: RowActionsMenuProps) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  const toggle = () => {
    if (open) {
      close();
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const menuW = 220;
    const left =
      placement === 'left'
        ? rect.right - menuW + window.scrollX
        : rect.left + window.scrollX;
    setPos({ top: rect.bottom + 6 + window.scrollY, left });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      )
        close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, close]);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      data-testid={testId ?? ''}
      className="relative inline-flex"
    >
      {/* Trigger */}
      <div ref={triggerRef} onClick={toggle} className="inline-flex">
        <AppButton
          variant="neutral"
          shape="circle"
          size="xs"
          icon={<BsThreeDotsVertical className="w-3.5 h-3.5" />}
          aria-label="Row actions"
          aria-expanded={open}
        />
      </div>

      {/* Floating dropdown rendered in a portal */}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: pos.top,
              left: pos.left,
              width: 220,
              zIndex: 9999,
            }}
            className={classNames(
              'rounded-xl overflow-hidden',
              APP_BG.popover,
              'border border-gray-200/80 dark:border-white/[0.08]',
              'shadow-[0_4px_6px_-1px_rgba(0,0,0,0.07),0_10px_28px_-4px_rgba(0,0,0,0.13)]',
              'dark:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.4),0_12px_32px_-4px_rgba(0,0,0,0.6)]',
            )}
          >
            {/* Accent bar */}
            <div className="h-[2px] w-full bg-gradient-to-r from-primary via-secondary to-tertiary" />

            {/* Header */}
            <div className="px-3 py-2 border-b border-gray-100 dark:border-white/[0.05]">
              <span className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-gray-400 dark:text-gray-600 select-none">
                {menuTitle ?? 'Actions'}
              </span>
            </div>

            {/* Items */}
            <div className="p-1.5">
              {actions.map((action, i) => (
                <React.Fragment key={i}>
                  {/* Section divider */}
                  {action.divider && (
                    <div className="flex items-center gap-2 px-1.5 py-1.5">
                      <div className="h-px flex-1 bg-gray-100 dark:bg-white/[0.06]" />
                      {action.sectionLabel && (
                        <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 dark:text-gray-600">
                          {action.sectionLabel}
                        </span>
                      )}
                      <div className="h-px flex-1 bg-gray-100 dark:bg-white/[0.06]" />
                    </div>
                  )}

                  <button
                    disabled={action.disabled}
                    title={action.tooltip}
                    onClick={() => {
                      if (!action.disabled) {
                        action.onClick?.();
                        close();
                      }
                    }}
                    style={{
                      outline: 'none',
                      boxShadow: 'none',
                      border: 'none',
                    }}
                    className={classNames(
                      'group w-full flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] bg-transparent text-left transition-colors duration-100',
                      action.disabled
                        ? 'cursor-not-allowed opacity-45'
                        : action.danger
                          ? 'cursor-pointer hover:bg-red-50 dark:hover:bg-red-950/40'
                          : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.05]',
                    )}
                    data-testid={testId ? tid(`${testId}-${action.label}`): undefined}
                  >
                    {/* Icon */}
                    <span
                      className={classNames(
                        'flex-shrink-0 w-4 h-4 transition-colors duration-100',
                        action.disabled
                          ? 'text-gray-300 dark:text-gray-700'
                          : action.danger
                            ? 'text-red-400 group-hover:text-red-500'
                            : 'text-gray-400 dark:text-gray-500 group-hover:text-primary',
                      )}
                    >
                      {action.icon}
                    </span>

                    {/* Label */}
                    <span
                      className={classNames(
                        'flex-1 text-[13px] font-medium leading-none select-none',
                        action.disabled
                          ? 'text-gray-400 dark:text-gray-600'
                          : action.danger
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-700 dark:text-gray-200',
                      )}
                    >
                      {action.label}
                    </span>

                    {/* Right: Soon badge or animated chevron */}
                    {action.disabled ? (
                      <span className="flex-shrink-0 text-[10px] font-semibold tracking-wide text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-white/[0.06] rounded px-1.5 py-0.5">
                        Soon
                      </span>
                    ) : (
                      <LuChevronRight
                        className={classNames(
                          'flex-shrink-0 w-3.5 h-3.5 opacity-0 group-hover:opacity-100',
                          '-translate-x-1 group-hover:translate-x-0 transition-all duration-100',
                          action.danger
                            ? 'text-red-400'
                            : 'text-gray-400 dark:text-gray-500',
                        )}
                      />
                    )}
                  </button>
                </React.Fragment>
              ))}
            </div>

            <div className="h-1" />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default RowActionsMenu;
